import { BookOpen, CalendarDays, Check, Columns3, Flame, Inbox, Layers, Monitor, Moon, RadioTower, RefreshCw, Settings, Sun, SunMedium, Target, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import { useEffect, useMemo } from "react";
import { Button } from "./ui/Button";
import { isInboxTask } from "../lib/inboxCore";
import { useTaskStore } from "../stores/taskStore";
import { useProjectStore } from "../stores/projectStore";
import { useSyncStore } from "../stores/syncStore";
import { useUiStore } from "../stores/uiStore";
import { FilterMode, TaskStats } from "../lib/tauri";

const filters: { key: FilterMode; label: string; icon: typeof Sun; count: keyof TaskStats | null }[] = [
  { key: "today", label: "今日", icon: Sun, count: "today" },
  { key: "active", label: "未完了", icon: Inbox, count: "active" },
  { key: "all", label: "全部", icon: Layers, count: "total" },
  { key: "completed", label: "完了", icon: Check, count: "completed" },
  { key: "high", label: "高優先", icon: Flame, count: "high" },
  { key: "trashed", label: "ゴミ箱", icon: Trash2, count: null },
];

const primaryFilters = filters.filter(({ key }) => ["today", "active", "high"].includes(key));
const secondaryFilters = filters.filter(({ key }) => ["all", "completed", "trashed"].includes(key));

const themeOptions: Array<{ value: "system" | "light" | "dark"; label: string; icon: typeof Sun }> = [
  { value: "system", label: "自動", icon: Monitor },
  { value: "light", label: "ライト", icon: SunMedium },
  { value: "dark", label: "ダーク", icon: Moon },
];

interface SidebarProps {
  commandMode?: boolean;
}

export function Sidebar({ commandMode = false }: SidebarProps) {
  const { filter, stats, setFilter } = useTaskStore();
  const allTasks = useTaskStore((s) => s.allTasks);
  const projects = useProjectStore((s) => s.projects);
  const projectsLoaded = useProjectStore((s) => s.loaded);
  const loadProjects = useProjectStore((s) => s.load);
  // projectStore は LaneBoard 表示時にのみ load されるため、司令塔起動直後は projects=[]。
  // 未ロード状態だと全タスクが孤児判定されバッジが膨張するので、ここで一度だけロードする。
  useEffect(() => {
    if (!projectsLoaded) void loadProjects();
  }, [projectsLoaded, loadProjects]);
  const knownProjectIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);
  // 未ロード時は孤児判定が信用できないため 0 を表示する（膨張防止）。
  const inboxCount = useMemo(
    () => (projectsLoaded ? allTasks.filter((t) => isInboxTask(t, knownProjectIds)).length : 0),
    [projectsLoaded, allTasks, knownProjectIds]
  );
  const syncNow = useSyncStore((s) => s.syncNow);
  const status = useSyncStore((s) => s.status);
  const appView = useUiStore((s) => s.appView);
  const setAppView = useUiStore((s) => s.setAppView);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const saveSettings = useSyncStore((s) => s.saveSettings);
  // 同期ステータスは D1 クラウド同期の実態（state + lastSyncAt）で表示する。
  // Notion 廃止後も「未同期/同期中/同期済み/同期エラー」を正直に出す。
  const hasSynced = Boolean(status.lastSyncAt && status.state !== "error");
  const dot =
    status.state === "error"
      ? "bg-danger-500"
      : status.state === "syncing"
      ? "bg-warning-500"
      : hasSynced
      ? "bg-success-500"
      : "bg-dim";
  const syncLabel =
    status.state === "syncing"
      ? "同期中"
      : status.state === "error"
      ? "同期エラー"
      : hasSynced
      ? "同期済み"
      : "未同期";
  const advancedActive =
    ["goals", "lanes", "knowledge", "calendar"].includes(appView) ||
    (appView === "tasks" && ["all", "completed", "trashed"].includes(filter));

  const handleThemeChange = (value: "system" | "light" | "dark") => {
    setTheme(value);
    void saveSettings({ theme: value });
  };
  return (
    <aside
      className={
        commandMode
          ? "flex w-[260px] shrink-0 flex-col border-r border-border bg-card text-text dark:border-white/10 dark:bg-[#111113] dark:text-zinc-200"
          : "flex w-[260px] shrink-0 flex-col border-r border-border bg-card dark:border-border-dark dark:bg-card-dark"
      }
    >
      <div className="p-6">
        <h1 className={`flex items-center gap-2 text-2xl font-bold tracking-tight ${commandMode ? "text-text dark:text-white" : ""}`}>
          <img src="/flow-logo.png" alt="" className="h-7 w-7" />
          Flow
        </h1>
        <div className={`mt-1 text-xs font-semibold ${commandMode ? "text-muted dark:text-zinc-400" : "text-muted dark:text-muted-dark"}`}>司令塔</div>
        <div className={`mt-1 flex items-center gap-1.5 text-xs ${commandMode ? "text-dim dark:text-zinc-500" : "text-muted dark:text-muted-dark"}`}>
          <span className={clsx("h-1.5 w-1.5 rounded-full", dot)} />
          {syncLabel}
        </div>
      </div>
      <nav className="mt-2 flex-1 overflow-y-auto px-3">
        <div className="mb-2 px-3 text-[10px] font-semibold text-dim">COMMAND</div>
        <button
          className={clsx(
            "mb-3 flex h-11 w-full items-center gap-2 rounded-md px-3 text-sm transition",
            appView === "command"
              ? commandMode
                ? "bg-brand-500/15 font-semibold text-brand-700 dark:bg-indigo-400/20 dark:text-indigo-200"
                : "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
              : commandMode
              ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
              : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
          )}
          onClick={() => setAppView("command")}
        >
          <span className={clsx("h-5 w-[3px] rounded-full", appView === "command" ? "bg-brand-500" : "bg-transparent")} />
          <RadioTower className="h-4 w-4" />
          <span className="flex-1 text-left">Mission Control</span>
          <span className="font-mono text-xs">{stats.active}</span>
        </button>
        <div className="mb-2 px-3 text-[10px] font-semibold text-dim">TASKS</div>
        {primaryFilters.map(({ key, label, icon: Icon, count }) => (
          <button
            key={key}
            className={clsx(
              "mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm transition",
              appView === "tasks" && filter === key
                ? commandMode
                  ? "bg-brand-500/10 font-semibold text-brand-700 dark:bg-indigo-400/15 dark:text-indigo-200"
                  : "bg-brand-500/10 font-semibold text-brand-700 dark:text-brand-200"
                : commandMode
                ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
                : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
            )}
            onClick={() => {
              setAppView("tasks");
              void setFilter(key);
            }}
          >
            <span className={clsx("h-5 w-[3px] rounded-full", appView === "tasks" && filter === key ? "bg-brand-500" : "bg-transparent")} />
            <Icon className="h-4 w-4" />
            <span className="flex-1 text-left">{label}</span>
            {count && <span className="font-mono text-xs">{stats[count]}</span>}
          </button>
        ))}
        <button
          className={clsx(
            "mb-3 flex h-11 w-full items-center gap-2 rounded-md px-3 text-sm transition",
            appView === "inbox"
              ? commandMode
                ? "bg-brand-500/15 font-semibold text-brand-700 dark:bg-indigo-400/20 dark:text-indigo-200"
                : "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
              : commandMode
              ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
              : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
          )}
          onClick={() => setAppView("inbox")}
        >
          <span className={clsx("h-5 w-[3px] rounded-full", appView === "inbox" ? "bg-brand-500" : "bg-transparent")} />
          <Inbox className="h-4 w-4" />
          <span className="flex-1 text-left">インボックス</span>
          {inboxCount > 0 && <span className="font-mono text-xs">{inboxCount}</span>}
        </button>
        <details className="group mt-4" open={advancedActive}>
          <summary
            className={clsx(
              "mb-2 flex h-9 cursor-pointer list-none items-center justify-between rounded-md px-3 text-xs font-semibold transition marker:hidden",
              commandMode
                ? "text-dim hover:bg-hover hover:text-text dark:text-zinc-500 dark:hover:bg-white/5 dark:hover:text-zinc-200"
                : "text-dim hover:bg-hover hover:text-text dark:hover:bg-hover-dark dark:hover:text-text-dark"
            )}
          >
            <span>その他</span>
            <span className="text-[10px] transition group-open:rotate-180">⌄</span>
          </summary>
          <button
            className={clsx(
              "mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm transition",
              appView === "goals"
                ? commandMode
                  ? "bg-brand-500/15 font-semibold text-brand-700 dark:bg-indigo-400/20 dark:text-indigo-200"
                  : "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
                : commandMode
                ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
                : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
            )}
            onClick={() => setAppView("goals")}
          >
            <span className={clsx("h-5 w-[3px] rounded-full", appView === "goals" ? "bg-brand-500" : "bg-transparent")} />
            <Target className="h-4 w-4" />
            <span className="flex-1 text-left">ゴール</span>
          </button>
          <button
            className={clsx(
              "mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm transition",
              appView === "lanes"
                ? commandMode
                  ? "bg-brand-500/15 font-semibold text-brand-700 dark:bg-indigo-400/20 dark:text-indigo-200"
                  : "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
                : commandMode
                ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
                : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
            )}
            onClick={() => setAppView("lanes")}
          >
            <span className={clsx("h-5 w-[3px] rounded-full", appView === "lanes" ? "bg-brand-500" : "bg-transparent")} />
            <Columns3 className="h-4 w-4" />
            <span className="flex-1 text-left">案件レーン</span>
          </button>
          <button
            className={clsx(
              "mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm transition",
              appView === "knowledge"
                ? commandMode
                  ? "bg-brand-500/15 font-semibold text-brand-700 dark:bg-indigo-400/20 dark:text-indigo-200"
                  : "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
                : commandMode
                ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
                : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
            )}
            onClick={() => setAppView("knowledge")}
          >
            <span className={clsx("h-5 w-[3px] rounded-full", appView === "knowledge" ? "bg-brand-500" : "bg-transparent")} />
            <BookOpen className="h-4 w-4" />
            <span className="flex-1 text-left">ナレッジ</span>
          </button>
          <button
            className={clsx(
              "mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm transition",
              appView === "calendar"
                ? commandMode
                  ? "bg-brand-500/15 font-semibold text-brand-700 dark:bg-indigo-400/20 dark:text-indigo-200"
                  : "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
                : commandMode
                ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
                : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
            )}
            onClick={() => setAppView("calendar")}
          >
            <span className={clsx("h-5 w-[3px] rounded-full", appView === "calendar" ? "bg-brand-500" : "bg-transparent")} />
            <CalendarDays className="h-4 w-4" />
            <span className="flex-1 text-left">カレンダー</span>
          </button>
          {secondaryFilters.map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              className={clsx(
                "mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm transition",
                appView === "tasks" && filter === key
                  ? commandMode
                    ? "bg-brand-500/10 font-semibold text-brand-700 dark:bg-indigo-400/15 dark:text-indigo-200"
                    : "bg-brand-500/10 font-semibold text-brand-700 dark:text-brand-200"
                  : commandMode
                  ? "text-muted hover:bg-hover hover:text-text dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
                  : "text-muted hover:bg-hover hover:text-text dark:text-muted-dark dark:hover:bg-hover-dark dark:hover:text-text-dark"
              )}
              onClick={() => {
                setAppView("tasks");
                void setFilter(key);
              }}
            >
              <span className={clsx("h-5 w-[3px] rounded-full", appView === "tasks" && filter === key ? "bg-brand-500" : "bg-transparent")} />
              <Icon className="h-4 w-4" />
              <span className="flex-1 text-left">{label}</span>
              {count && <span className="font-mono text-xs">{stats[count]}</span>}
            </button>
          ))}
        </details>
      </nav>
      <div className={`space-y-2 border-t p-3 ${commandMode ? "border-border dark:border-white/10" : "border-border dark:border-border-dark"}`}>
        {/* Theme toggle (segmented control) */}
        <div className={`flex items-center gap-1 rounded-md border p-0.5 ${commandMode ? "border-border bg-bg dark:border-white/10 dark:bg-zinc-950" : "border-border bg-bg dark:border-border-dark dark:bg-bg-dark"}`}>
          {themeOptions.map(({ value, label, icon: Icon }) => {
            const active = theme === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleThemeChange(value)}
                className={clsx(
                  "flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium transition",
                  active
                    ? commandMode
                      ? "bg-card text-text shadow-sm dark:bg-zinc-800 dark:text-zinc-100"
                      : "bg-card text-text shadow-sm dark:bg-card-dark dark:text-text-dark"
                    : commandMode
                    ? "text-muted hover:text-text dark:text-zinc-500 dark:hover:text-zinc-200"
                    : "text-muted hover:text-text dark:text-muted-dark dark:hover:text-text-dark"
                )}
                aria-pressed={active}
                aria-label={`テーマ: ${label}`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
        <Button
          variant="outline"
          className="w-full justify-start"
          leftIcon={<RefreshCw className="h-4 w-4" />}
          disabled={status.state === "syncing"}
          onClick={() => void syncNow()}
        >
          {status.state === "syncing" ? "同期中…" : "今すぐ同期"}
        </Button>
        <Button variant="ghost" className="w-full justify-start" leftIcon={<Settings className="h-4 w-4" />} onClick={() => setSettingsOpen(true)}>設定</Button>
      </div>
    </aside>
  );
}
